/**
 * RangeProductTile theme (KINDW-99 / FSD PLP-03)
 *
 * Hero image behaviour (2-image hover swap, 3+ carousel) is centralized in
 * `ProductTileImageGallery` + `product-tile-image-behavior.js`.
 */
import {
  PRODUCT_TILE_HOVER_SWAP_LAYER,
  PRODUCT_TILE_HOVER_SWAP_PRIMARY,
  PRODUCT_TILE_HOVER_SWAP_SECONDARY,
  PRODUCT_TILE_HOVER_SWAP_WRAPPER,
  PRODUCT_TILE_IMAGE_BEHAVIOR_SX,
} from '@amblique/king-living/app/theme/components/project/product-tile-image-theme';
const TILE_IMAGE_W = 317;
const TILE_IMAGE_H = 400;

const SWATCH_DIAMETER = '12px';

const searchResultsLabelRegText = {
  color: 'grayAlpha.70',
  fontFamily: 'body',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '120%',
};

const RANGE_TILE_BADGE_BASE = {
  alignItems: 'center',
  borderRadius: '4px',
  color: '#FFFFFF',
  display: 'flex',
  gap: '8px',
  fontFamily: 'heading',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  h: {base: 'auto', lg: '22px'},
  letterSpacing: 0,
  lineHeight: '120%',
  minH: {base: 'auto', lg: '22px'},
  paddingX: '8px',
  paddingY: '4px',
  textTransform: 'none',
};

const RangeProductTile = {
  parts: [
    'container',
    'link',
    'imageWrapper',
    'aspectRatio',
    'swiperContainer',
    'hoverSwapWrapper',
    'hoverSwapLayer',
    'hoverSwapPrimary',
    'hoverSwapSecondary',
    'badgeStack',
    'saleBadge',
    'newBadge',
    'placeholderWrapper',
    'placeholderImage',
    'infoSection',
    'copyStack',
    'swatchesRow',
    'swatchItem',
    'swatchMore',
    'title',
    'subtitle',
    'priceSection',
    'pricePrimaryRow',
    'promoCallout',
    'priceCurrent',
    'priceFullRow',
    'priceFullLabel',
    'priceFullStrike',
  ],
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    },
    link: {
      display: 'block',
      borderRadius: 'sm',
      textDecoration: 'none',
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brandColors.primaryDarkLinen',
        outlineOffset: '2px',
      },
      _hover: {textDecoration: 'none'},
    },
    imageWrapper: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 'unset',
      bg: 'brandColors.primaryLinen15',
      zIndex: 0,
      sx: PRODUCT_TILE_IMAGE_BEHAVIOR_SX,
    },
    aspectRatio: {
      ratio: TILE_IMAGE_W / TILE_IMAGE_H,
      w: '100%',
    },
    swiperContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    hoverSwapWrapper: PRODUCT_TILE_HOVER_SWAP_WRAPPER,
    hoverSwapLayer: PRODUCT_TILE_HOVER_SWAP_LAYER,
    hoverSwapPrimary: PRODUCT_TILE_HOVER_SWAP_PRIMARY,
    hoverSwapSecondary: PRODUCT_TILE_HOVER_SWAP_SECONDARY,
    // "Image not available" placeholder layout — fills the tile cell, centres the SVG.
    // The cream bg is already on `imageWrapper`, so we leave the placeholder wrapper transparent
    // to avoid double-painting the same colour.
    placeholderWrapper: {
      alignItems: 'center',
      bg: 'transparent',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    },
    // Cap the SVG so the icon reads as a "missing image" affordance, not a stretched billboard.
    // Mirrors the PDP `placeholderImage` rule (75%) for visual consistency.
    placeholderImage: {
      height: 'auto',
      maxHeight: '60%',
      maxWidth: '60%',
      objectFit: 'contain',
      userSelect: 'none',
    },
    // Badge cluster — Sale + New side by side (row wrap).
    badgeStack: {
      position: 'absolute',
      top: {base: '12px', lg: '20px'},
      left: {base: '12px', lg: '20px'},
      zIndex: 2,
      display: 'flex',
      flexFlow: 'row wrap',
      alignItems: 'flex-start',
      gap: 2,
      pointerEvents: 'none',
      '> *': {
        pointerEvents: 'auto',
      },
    },
    saleBadge: {
      ...RANGE_TILE_BADGE_BASE,
      bg: 'secondary.newSaleRed',
    },
    newBadge: {
      ...RANGE_TILE_BADGE_BASE,
      bg: 'brandColors.greyscale90',
    },
    infoSection: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: {base: '10px', lg: 4},
      paddingTop: {base: 4, lg: 4},
      paddingBottom: {base: 4, lg: 4},
      paddingX: {base: 4, lg: 0},
    },
    copyStack: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    },
    swatchesRow: {
      alignItems: 'center',
      bottom: {base: '30px', lg: '41px'},
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '5px',
      justifyContent: 'center',
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      zIndex: 3,
    },
    swatchItem: {
      width: SWATCH_DIAMETER,
      height: SWATCH_DIAMETER,
      borderRadius: '3px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      flexShrink: 0,
      pointerEvents: 'none',
      backgroundColor: 'greyscale.10',
    },
    swatchMore: {
      color: 'greyscale.80',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
      marginLeft: '3px',
      pointerEvents: 'none',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    title: {
      color: 'grayAlpha.90',
      fontFamily: 'heading',
      fontSize: {base: '14px', lg: '18px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: 1.2,
      noOfLines: 3,
    },
    subtitle: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: {base: '12px', lg: '14px'},
      fontWeight: 400,
      lineHeight: 1.4,
      noOfLines: 2,
    },
    priceSection: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    // Sale line + full-price line always stack (client: full price on its own row).
    pricePrimaryRow: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      gap: 1,
      width: '100%',
    },
    promoCallout: {
      alignSelf: 'stretch',
      color: 'secondary.newSaleRed',
      fontFamily: 'heading',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: '120%',
      maxW: '100%',
      wordBreak: 'break-word',
      '& p': {
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontStyle: 'inherit',
        fontWeight: 'inherit',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',
        mb: '0px',
      },
      '& p:last-child': {
        mb: '0px',
      },
      '& ul, & ol': {
        mb: '0px',
        pl: 4,
      },
    },
    priceCurrent: {
      color: 'grayAlpha.90',
      fontFamily: 'heading',
      fontSize: {base: '14px', lg: '16px'},
      fontWeight: 500,
      lineHeight: 1.2,
    },
    priceFullRow: {
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1,
      position: 'relative',
      top: '1px',
    },
    priceFullLabel: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
    },
    // Full list price — no strikethrough (client sign-off).
    priceFullStrike: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '120%',
      textDecoration: 'none',
    },
  },
  variants: {
    default: {},

    searchResults: {
      copyStack: {
        minWidth: 0,
        width: '100%',
      },
      title: {
        minWidth: 0,
        noOfLines: 3,
        width: '100%',
      },
      subtitle: {
        ...searchResultsLabelRegText,
        minWidth: 0,
        noOfLines: 2,
        width: '100%',
      },
      priceFullLabel: searchResultsLabelRegText,
      priceFullStrike: {
        ...searchResultsLabelRegText,
        textDecoration: 'none',
      },
    },

    /** Cart upsell recommender — designer typography (Aktiv Grotesk via theme `body`). */
    upsell: {
      copyStack: {
        gap: 2,
      },
      infoSection: {
        gap: 4,
        p: 4,
        paddingX: 0,
      },
      promoCallout: {
        color: 'secondary.newSaleRed',
        fontFamily: 'body',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: 0,
        lineHeight: 1.2,
        wordBreak: 'break-word',
      },
      priceCurrent: {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: {base: '14px', lg: '16px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,
      },
      priceFullLabel: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
      },
      priceFullStrike: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        textDecoration: 'none',
      },
      pricePrimaryRow: {
        alignItems: 'flex-start',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        gap: 1,
      },
      priceSection: {
        gap: 0,
        marginTop: 0,
      },
      subtitle: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: {base: '12px', lg: '14px'},
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        noOfLines: 2,
      },
      title: {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: {base: '14px', lg: '18px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.4,
        noOfLines: 3,
      },
    },
  },
  defaultProps: {
    variant: 'default',
  },
};

export default RangeProductTile;
